import React, { useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import NavBar from "../components/NavigationBar";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations/translations";
import ScrollFadeSection from "../components/ScrollFadeSection";

const SourcesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language].sources;

  const myth1Ref = useRef<HTMLDivElement | null>(null);
  const myth2Ref = useRef<HTMLDivElement | null>(null);
  const myth3Ref = useRef<HTMLDivElement | null>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => {
    const element = ref.current;
    if (!element) return;

    const navOffset = 140; // Höhe der Navbar + etwas Luft

    const rect = element.getBoundingClientRect();
    const absoluteY = window.scrollY + rect.top - navOffset;

    window.scrollTo({
      top: absoluteY,
      behavior: "smooth",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavBar />

      <Box
        sx={{
          mt: 10,
          flex: 1,
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
          paddingTop: 4,
          pb: 6,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1200, display: "flex", gap: 4 }}>
          {/* ------------------ SIDE NAV (DESKTOP) ------------------ */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              minWidth: 220,
              position: "sticky",
              top: 96,
              alignSelf: "flex-start",
              gap: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ textTransform: "uppercase", color: "#777", mb: 1 }}
            >
              Myths
            </Typography>

            <Button
              variant="outlined"
              size="small"
              onClick={() => scrollTo(myth1Ref)}
              sx={{
                justifyContent: "flex-start",
                borderRadius: "999px",
                borderColor: "#5B8CB9",
                color: "#5B8CB9",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#4C7AA0",
                  backgroundColor: "rgba(91, 140, 185, 0.06)",
                },
              }}
            >
              {t.titleMyth1}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => scrollTo(myth2Ref)}
              sx={{
                justifyContent: "flex-start",
                borderRadius: "999px",
                borderColor: "#5B8CB9",
                color: "#5B8CB9",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#4C7AA0",
                  backgroundColor: "rgba(91, 140, 185, 0.06)",
                },
              }}
            >
              {t.titleMyth2}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => scrollTo(myth3Ref)}
              sx={{
                justifyContent: "flex-start",
                borderRadius: "999px",
                borderColor: "#5B8CB9",
                color: "#5B8CB9",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#4C7AA0",
                  backgroundColor: "rgba(91, 140, 185, 0.06)",
                },
              }}
            >
              {t.titleMyth3}
            </Button>
          </Box>

          {/* ------------------ MAIN CONTENT ------------------ */}
          <Box sx={{ flex: 1 }}>
            {/* Titel + Beschreibung */}
            <Typography variant="h4" gutterBottom>
              {t.title}
            </Typography>

            <Typography variant="body1" paragraph>
              {t.description}
            </Typography>

            {/* MOBILE NAV (oben über den Boxen) */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                gap: 1,
                flexWrap: "wrap",
                mb: 3,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => scrollTo(myth1Ref)}
                sx={{
                  borderRadius: "999px",
                  borderColor: "#5B8CB9",
                  color: "#5B8CB9",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": {
                    borderColor: "#4C7AA0",
                    backgroundColor: "rgba(91, 140, 185, 0.06)",
                  },
                }}
              >
                {t.titleMyth1}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => scrollTo(myth2Ref)}
                sx={{
                  borderRadius: "999px",
                  borderColor: "#5B8CB9",
                  color: "#5B8CB9",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": {
                    borderColor: "#4C7AA0",
                    backgroundColor: "rgba(91, 140, 185, 0.06)",
                  },
                }}
              >
                {t.titleMyth2}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => scrollTo(myth3Ref)}
                sx={{
                  borderRadius: "999px",
                  borderColor: "#5B8CB9",
                  color: "#5B8CB9",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": {
                    borderColor: "#4C7AA0",
                    backgroundColor: "rgba(91, 140, 185, 0.06)",
                  },
                }}
              >
                {t.titleMyth3}
              </Button>
            </Box>

            {/* ------------------ MYTHOS #1 ------------------ */}
            <ScrollFadeSection>
              <Box
                ref={myth1Ref}
                sx={{
                  mt: 3,
                  p: 3,
                  borderRadius: "20px",
                  border: "2px solid #5B8CB9",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  backgroundColor: "rgba(255,255,255,0.95)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontFamily: "Bangers", mb: 1 }}
                >
                  {t.titleMyth1}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.descriptionMyth1}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.debunkingMyth1}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {t.sources}
                </Typography>

                <ul>
                  <li>
                    <em>[1] KI: Die Illusion des Denkens</em> :{" "}
                    <a href="https://scilogs.spektrum.de/hirn-und-weg/ki-die-illusion-des-denkens/">
                      https://scilogs.spektrum.de/hirn-und-weg/ki-die-illusion-des-denkens/
                    </a>
                  </li>
                  <li>
                    <em>
                      [2] Was der KI fehlt, ist das, was uns menschlich macht
                    </em>{" "}
                    :{" "}
                    <a href="https://www.herder.de/communio/kolumnen/hartl-aber-herzlich/was-der-ki-fehlt-ist-das-was-uns-menschlich-macht-kuenstliches-bewusstsein/">
                      https://www.herder.de/communio/kolumnen/hartl-aber-herzlich/was-der-ki-fehlt-ist-das-was-uns-menschlich-macht-kuenstliches-bewusstsein/
                    </a>
                  </li>
                  <li>
                    <em>
                      [3] Stochastic parrots and the illusion of understanding
                      in AI
                    </em>{" "}
                    :{" "}
                    <a href="https://www.bigroup.com.au/stochastic-parrots-language-models/">
                      https://www.bigroup.com.au/stochastic-parrots-language-models/
                    </a>
                  </li>
                  <li>
                    <em>
                      [4] Intelligenz und Bewusstsein. Oder: Ist KI wirklich KI?
                    </em>{" "}
                    :{" "}
                    <a href="https://www.bpb.de/shop/zeitschriften/apuz/kuenstliche-intelligenz-2023/541495/intelligenz-und-bewusstsein/">
                      https://www.bpb.de/shop/zeitschriften/apuz/kuenstliche-intelligenz-2023/541495/intelligenz-und-bewusstsein/
                    </a>
                  </li>
                </ul>
              </Box>
            </ScrollFadeSection>

            {/* ------------------ MYTHOS #2 ------------------ */}
            <ScrollFadeSection>
              <Box
                ref={myth2Ref}
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: "20px",
                  border: "2px solid #5B8CB9",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  backgroundColor: "rgba(255,255,255,0.95)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontFamily: "Bangers", mb: 1 }}
                >
                  {t.titleMyth2}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.descriptionMyth2}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.debunkingMyth2}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {t.sources}
                </Typography>

                <ul>
                  <li>
                    <em>[1] GDPR - General Data Protection Regulation</em> :{" "}
                    <a href="https://gdpr.eu/">
                      https://gdpr.eu/
                    </a>
                  </li>
                  <li>
                    <em>[2] Cisco Consumer Privacy Survey 2024</em> :{" "}
                    <a href="https://www.cisco.com/c/en/us/products/security/security-reports.html">
                      https://www.cisco.com/c/en/us/products/security/security-reports.html
                    </a>
                  </li>
                  <li>
                    <em>
                      [3] Understanding what Large Language Models can and
                      cannot know
                    </em>{" "}
                    :{" "}
                    <a href="https://hai.stanford.edu/news/understanding-limits-large-language-models">
                      https://hai.stanford.edu/news/understanding-limits-large-language-models
                    </a>
                  </li>
                  <li>
                    <em>[4] Why AI cannot access personal data</em> :{" "}
                    <a href="https://www.ibm.com/topics/what-is-artificial-intelligence">
                      https://www.ibm.com/topics/what-is-artificial-intelligence
                    </a>
                  </li>
                </ul>
              </Box>
            </ScrollFadeSection>

            {/* ------------------ MYTHOS #3 ------------------ */}
            <ScrollFadeSection>
              <Box
                ref={myth3Ref}
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: "20px",
                  border: "2px solid #5B8CB9",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  backgroundColor: "rgba(255,255,255,0.95)",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontFamily: "Bangers", mb: 1 }}
                >
                  {t.titleMyth3}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.descriptionMyth3}
                </Typography>

                <Typography
                  component="p"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {t.debunkingMyth3}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {t.sources}
                </Typography>

                <ul>
                  <li>
                    <em>
                      [1] World Economic Forum – The Future of Jobs Report 2023
                    </em>{" "}
                    :{" "}
                    <a href="https://www.weforum.org/publications/the-future-of-jobs-report-2023/">
                      https://www.weforum.org/publications/the-future-of-jobs-report-2023/
                    </a>
                  </li>
                  <li>
                    <em>
                      [2] OECD – The impact of artificial intelligence on the
                      labour market
                    </em>{" "}
                    :{" "}
                    <a href="https://www.oecd.org/en/publications/2021/01/the-impact-of-artificial-intelligence-on-the-labour-market_a4b9cac2.html">
                      https://www.oecd.org/en/publications/2021/01/the-impact-of-artificial-intelligence-on-the-labour-market_a4b9cac2.html
                    </a>
                  </li>
                  <li>
                    <em>
                      [3] ILO – Artificial intelligence and the future of work:
                      Will AI replace our jobs?
                    </em>{" "}
                    :{" "}
                    <a href="https://unric.org/en/artificial-intelligence-and-the-future-of-work-will-ai-replace-our-jobs/">
                      https://unric.org/en/artificial-intelligence-and-the-future-of-work-will-ai-replace-our-jobs/
                    </a>
                  </li>
                  <li>
                    <em>
                      [4] World Economic Forum – AI: 3 ways artificial
                      intelligence is changing the future of work
                    </em>{" "}
                    :{" "}
                    <a href="https://www.weforum.org/stories/2023/08/ai-artificial-intelligence-changing-the-future-of-work-jobs/">
                      https://www.weforum.org/stories/2023/08/ai-artificial-intelligence-changing-the-future-of-work-jobs/
                    </a>
                  </li>
                </ul>
              </Box>
            </ScrollFadeSection>

            {/* ------------------ BOTTOM TEXT ------------------ */}
            <ScrollFadeSection>
              <Typography variant="body1" paragraph sx={{ mt: 4 }}>
                {t.bottomText}
              </Typography>
            </ScrollFadeSection>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default SourcesPage;
